import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { propertyRoute } from '@/mocks/propertyRoutes';

type PricingSnapshot = {
  generated_at: string;
  search: {
    arrival: string;
    departure: string;
    adults: number;
    children: number;
    pets: number;
    note: string;
  };
  results: PricingProperty[];
};

type PricingProperty = {
  property_id: number;
  name: string;
  bedrooms: number;
  bathrooms: number;
  max_guests: number;
  thumbnail: string;
  headline: string;
  nightly_rate_min: number | null;
  nightly_rate_max: number | null;
  quote: {
    arrival: string;
    departure: string;
    nights: number | null;
    rent: number;
    fees: number;
    taxes: number;
    total: number;
    charges: {
      type: string;
      description: string;
      amount: number;
    }[];
  };
};

const snapshotUrl = '/ownerrez-poc/pricing-proof.json';

const routeByName: Record<string, string> = {
  'The Belvedere': propertyRoute('belvedere'),
  "Hannah's Pearl": propertyRoute('hannahs-pearl'),
  'Sea Dreams': propertyRoute('sea-dreams'),
};

function money(value: number) {
  const roundedValue = Math.ceil(value);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(roundedValue);
}

function shortDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function rateRange(property: PricingProperty) {
  if (!property.nightly_rate_min || !property.nightly_rate_max) return 'Rate range available';
  return `${money(property.nightly_rate_min)}-${money(property.nightly_rate_max)} nightly range`;
}

function PricingCard({ property }: { property: PricingProperty }) {
  const nightlyAverage = property.quote.nights ? property.quote.rent / property.quote.nights : property.quote.rent;
  const href = routeByName[property.name] ?? '/vacation-search-results';

  return (
    <article className="grid overflow-hidden rounded-lg border border-background-200/80 bg-background-100/70 shadow-[0_18px_50px_rgba(30,24,18,0.06)] lg:grid-cols-[0.92fr_1.08fr]">
      <div className="relative min-h-[320px] bg-background-200">
        <img
          src={property.thumbnail}
          alt={property.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 text-background-50">
          <p className="text-[11px] uppercase tracking-[0.22em] text-background-50/75">
            Selected dates
          </p>
          <h2 className="mt-2 font-heading text-4xl leading-tight md:text-5xl">
            {property.name}
          </h2>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="flex flex-wrap gap-2">
          <span className="rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-foreground-700">
            {property.bedrooms} bedrooms
          </span>
          <span className="rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-foreground-700">
            {property.bathrooms} bathrooms
          </span>
          <span className="rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-foreground-700">
            Sleeps {property.max_guests}
          </span>
        </div>

        <p className="mt-6 text-sm uppercase tracking-[0.18em] text-accent-700">
          {rateRange(property)}
        </p>
        <p className="mt-3 text-foreground-600 text-base leading-relaxed">
          {property.headline}
        </p>

        <div className="mt-7 rounded-lg border border-background-300 bg-background-50 p-5">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm text-foreground-500">
                Stay Total
              </p>
              <p className="mt-1 font-heading text-4xl text-foreground-950">
                {money(property.quote.total)}
              </p>
            </div>
            <div className="text-right text-sm text-foreground-500">
              <p>{property.quote.nights ?? 5} nights</p>
              <p>{money(nightlyAverage)} avg. rent/night</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 border-t border-background-200 pt-5 text-sm">
            <div>
              <p className="text-foreground-500">Rent</p>
              <p className="mt-1 font-medium text-foreground-950">{money(property.quote.rent)}</p>
            </div>
            <div>
              <p className="text-foreground-500">Fees</p>
              <p className="mt-1 font-medium text-foreground-950">{money(property.quote.fees)}</p>
            </div>
            <div>
              <p className="text-foreground-500">Taxes</p>
              <p className="mt-1 font-medium text-foreground-950">{money(property.quote.taxes)}</p>
            </div>
          </div>
        </div>

        <details className="mt-5 rounded-lg border border-background-200 bg-background-50/70 p-4 text-sm text-foreground-600">
          <summary className="cursor-pointer font-medium text-foreground-800">
            See stay details
          </summary>
          <div className="mt-4 space-y-2">
            {property.quote.charges.map((charge, index) => (
              <div key={`${charge.description}-${index}`} className="flex justify-between gap-4 border-t border-background-200 pt-2">
                <span>{charge.description}</span>
                <span className="font-medium text-foreground-950">{money(charge.amount)}</span>
              </div>
            ))}
          </div>
        </details>

        <Link
          to={href}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-foreground-950 px-5 py-3 text-sm text-background-50 transition-colors hover:bg-accent-700"
        >
          View property page
          <i className="ri-arrow-right-up-line" />
        </Link>
      </div>
    </article>
  );
}

export default function OwnerRezPricingProofPage() {
  const [snapshot, setSnapshot] = useState<PricingSnapshot | null>(null);

  useEffect(() => {
    fetch(snapshotUrl)
      .then((response) => response.json())
      .then(setSnapshot)
      .catch(() => setSnapshot(null));
  }, []);

  const sortedResults = useMemo(
    () => [...(snapshot?.results ?? [])].sort((a, b) => a.quote.total - b.quote.total),
    [snapshot],
  );

  if (!snapshot) {
    return (
      <main className="min-h-screen bg-background-50 text-foreground-900 font-body">
        <Header />
        <section className="mx-auto max-w-[900px] px-5 pb-24 pt-40 md:px-10">
          <p className="mb-4 text-[11px] uppercase tracking-[0.22em] text-accent-700">
            Pricing preview
          </p>
          <h1 className="font-heading text-5xl text-foreground-950">
            Loading the pricing test...
          </h1>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />
      <section className="border-b border-background-200 bg-foreground-950 pb-16 pt-36 text-background-50 md:pb-24 md:pt-44">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-5 md:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="mb-5 text-[11px] uppercase tracking-[0.24em] text-accent-300">
              <span className="mr-3 inline-block h-px w-6 bg-accent-500 align-middle" />
              Private pricing preview
            </p>
            <h1 className="max-w-4xl font-heading text-5xl leading-[0.98] md:text-7xl lg:text-8xl">
              Search dates,
              <span className="block italic font-light text-background-100/80">see stay totals.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-background-100/78 md:text-lg">
              Choose dates and guest details, then see matching properties with stay totals
              presented in Inspire&apos;s own design.
            </p>
          </div>

          <div className="rounded-lg border border-background-50/15 bg-background-50/8 p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-accent-300">
              Sample search
            </p>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-background-100/55">Arrival</p>
                <p className="mt-1 text-lg text-background-50">{shortDate(snapshot.search.arrival)}</p>
              </div>
              <div>
                <p className="text-background-100/55">Departure</p>
                <p className="mt-1 text-lg text-background-50">{shortDate(snapshot.search.departure)}</p>
              </div>
              <div>
                <p className="text-background-100/55">Guests</p>
                <p className="mt-1 text-lg text-background-50">
                  {snapshot.search.adults + snapshot.search.children}
                </p>
              </div>
              <div>
                <p className="text-background-100/55">Pets</p>
                <p className="mt-1 text-lg text-background-50">{snapshot.search.pets}</p>
              </div>
            </div>
            <p className="mt-5 border-t border-background-50/15 pt-4 text-xs leading-relaxed text-background-100/55">
              Preview only. Nothing was saved as a booking.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-9 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700">
                Three matching examples
              </p>
              <h2 className="mt-3 font-heading text-4xl leading-tight text-foreground-950 md:text-5xl">
                Pricing for these dates,
                <span className="block italic font-light text-foreground-600">presented like Inspire.</span>
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-foreground-500">
              Sample refreshed {new Date(snapshot.generated_at).toLocaleString()}. In the live version,
              this would update quietly behind the scenes.
            </p>
          </div>

          <div className="space-y-8">
            {sortedResults.map((property) => (
              <PricingCard key={property.property_id} property={property} />
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
